/**
 * The Report Studio — `/marking/:officialId/report`.
 *
 * Authority: `05-reporting-marking.md` §A5, §B2, §B5a, §B6 and §B9, drawn to the agreed
 * mockup `docs/design/official-profile-detail-v1.html` — specifically the **Reporting
 * Section** at the bottom of that page, which is the layout the Technical Delegate signed
 * off:
 *
 * ```
 * .repband            Reporting Section  (+ lead paragraph)
 * panel               Marking Panel      (full width, the bars and the TMS strip)
 * .apptsum (5fr|7fr)  LEFT  TD Feedback
 *                     RIGHT AI Drafted Report, then TD Draft Report
 * ```
 *
 * **Run 21C rebuilt this screen.** It used to be a repband and a single composer panel — a
 * two-box `TD draft / preview` arrangement that the TD judged "not anywhere as good" as the
 * agreed draft. **Nothing was dropped in the move**: the closing-remarks composer, the
 * assembled preview exactly as TMS receives it, the 800-character counter, the stage
 * transitions (`AI Drafted` / `TD Drafted`), the push and its confirmation, `Copy For TMS`,
 * `Download Word`, the push receipt and the owning UM's read-only narrative are all still
 * here — re-housed inside the mockup's `TD Draft Report` panel, in the mockup's right-hand
 * column, beneath the mockup's `AI Drafted Report` panel.
 *
 * **The preview stays, where the mockup puts the box it describes.** The mockup draws no
 * preview — its two textareas are the AI draft and the TD draft — so the preview lives inside
 * the `TD Draft Report` panel, directly under the box it is the assembly of. It is less
 * prominent than it was, which is the mockup's arrangement and the TD's instruction; it is
 * not less true, and it is still the same `assembleReportText` the API pushes with.
 *
 * **Three independent reads.** The profile supplies the display name, the marking supplies the
 * bars, the report supplies the drafting surface. They fail apart: a refused marking read
 * leaves the report composer working and says where it failed, and a report the API refuses
 * (there is none about the TD, ruling R2′) leaves an honest empty state rather than a
 * composer with nothing behind it.
 *
 * **`Tournament Report` is not on this screen.** It is the TD's end-of-tournament report to
 * FIH, about the tournament and not about the official being written up here; it lives on the
 * Reporting page and in the masthead of every screen.
 */

import { Link, useParams } from "react-router-dom";

import { fetchMarkingPanel, fetchOfficial, fetchReport } from "../api/client.js";
import { ApiError } from "../api/client.js";
import type { MarkingPanelBody, ReportBody } from "../api/types.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { EmptyState, FailBanner, Skeleton } from "../app/states.js";
import { usePageKicker } from "../app/pageKicker.js";
import { useAsync } from "../app/useAsync.js";
import { AiClosingNotePanel } from "../features/marking/AiClosingNotePanel.js";
import { AiDraftPanel } from "../features/marking/AiDraftPanel.js";
import { MarkingPanel } from "../features/marking/MarkingPanel.js";
import { ReportComposer } from "../features/marking/ReportComposer.js";
import { TdFeedbackPanel, useTdNotes } from "../features/officials/ProfileDetailPanels.js";
import { REPORTING_SECTION_LEAD } from "./OfficialProfilePage.js";
import { FINAL_MARKING_KICKER } from "./MarkingPage.js";

export function ReportStudioPage(): React.JSX.Element {
  const { officialId } = useParams<{ officialId: string }>();
  const id = officialId ?? "";

  // No kicker is specified for the composer itself. It is the marking screen's other half —
  // reached from it, about the same one whole-tournament record — so it keeps §B3a's line
  // rather than inheriting Part A's, which is about the panel and not about one official.
  usePageKicker(FINAL_MARKING_KICKER);

  const official = useAsync(() => fetchOfficial(id), [id]);
  const panel = useAsync<MarkingPanelBody>(() => fetchMarkingPanel(id), [id]);
  const report = useAsync<ReportBody>(() => fetchReport(id), [id]);

  const name = official.status === "ready" ? official.data.mediaName : `TMS person ${id}`;

  /**
   * The TD's own quick notes on this official — run 51, and the fourth read of this screen.
   *
   * `TD Feedback` beside the composer is no longer a placeholder: it groups these notes by the
   * grading category each was filed against, which is exactly the evidence a TD wants in front
   * of him while he drafts. This page has no other notes read, so this adds one fetch and
   * duplicates none. It fails apart from the other three, as they do from each other.
   */
  const tdNotes = useTdNotes(id);
  const subjectRole = official.status === "ready" ? official.data.role : null;

  return (
    <>
      <div className="repband">
        <h2>
          Reporting Section
          {official.status === "ready" ? (
            <span className="md"> · {official.data.mediaName}</span>
          ) : null}
        </h2>
        <p>{REPORTING_SECTION_LEAD}</p>
        <HowToUse id="report-studio" title="How to use the Reporting Section">
          <li>
            <HowToLink to="marking-bars">Marking Panel</HowToLink> holds the bars. One marking
            per official, given once, at the end of the tournament; the Performance Outcome
            recomputes the moment a bar moves.
          </li>
          <li>
            <HowToLink to="td-feedback">TD Feedback</HowToLink> on the left is the evidence
            behind those bars — the comments are the evidence, the mark is the conclusion.
          </li>
          <li>
            <HowToLink to="ai-draft">AI Drafted Report</HowToLink> is read-only. Copy it into
            your own draft below and edit there, so the generated text and your version stay
            apart.
          </li>
          <li>
            On an umpire row, <HowToLink to="ai-closing-note">AI Closing-Note Suggestion</HowToLink>{" "}
            offers a 3–4 line starting point for your own closing remarks below — from your
            marks and your own quick notes on that umpire. Copy it in and edit it; it is never
            written there for you.
          </li>
          <li>
            Write your part in <HowToLink to="td-composer">TD Draft Report</HowToLink>. One
            report per official, covering the whole tournament.
          </li>
          <li>
            The <HowToLink to="report-preview">preview</HowToLink> inside that panel shows the
            finished report exactly as TMS will receive it, with the <b>TD</b> and <b>UM</b>{" "}
            prefixes added at the last moment rather than typed. The counter counts the whole
            preview, because the whole assembled report is what has to fit the TMS limit.
          </li>
          <li>
            On an umpire, the owning umpires manager writes the narrative; you write the closing
            remarks, and you push the whole report — both parts — with the agreed marks.
          </li>
          <li>
            <b>Push To TMS</b> goes through the <b>TMS Push</b> extension in this Chrome: it
            opens its own window, reads the official&rsquo;s marking in TMS, and shows every mark
            and the text. Nothing is saved until you Confirm there, and then only with Save
            Draft — a second push updates the same mark in TMS rather than making another. Every
            mark must be set first, because TMS saves the marks with the report.
          </li>
          <li>
            Nothing leaves this screen on its own. You move the report on, and you press the
            button that sends it. Without the extension, Copy For TMS and Download Word are
            always there.
          </li>
        </HowToUse>
        <span style={{ flex: 1 }} />
        <Link className="linkbtn-ext" to={`/officials/${id}`}>
          Official Profile
        </Link>
        <Link className="linkbtn-ext" to="/marking">
          All Officials
        </Link>
        <Link className="linkbtn-ext" to={`/marking/${id}`}>
          Marking Panel
        </Link>
      </div>

      {official.status === "error" ? <FailBanner error={official.error} /> : null}

      {/* ── The mockup's full-width Marking Panel ─────────────────────────── */}
      <div style={{ marginTop: 22 }} data-howto="marking-bars">
        {panel.status === "loading" ? <Skeleton rows={6} /> : null}
        {panel.status === "error" ? (
          panel.error instanceof ApiError && panel.error.status === 404 ? (
            <EmptyState
              title="There is no marking for this official."
              detail={panel.error.message}
            />
          ) : (
            <FailBanner error={panel.error} />
          )
        ) : null}
        {panel.status === "ready" ? (
          // `key` remounts the panel for a different official rather than mutating the one on
          // screen, which is what makes its seed state safe.
          <MarkingPanel key={`marks-${id}`} officialName={name} initialPanel={panel.data} />
        ) : null}
      </div>

      {/* ── The mockup's 5fr / 7fr report chain ───────────────────────────── */}
      <div className="apptsum">
        <TdFeedbackPanel role={subjectRole} notes={tdNotes} />

        <div className="stackcol">
          {report.status === "loading" ? (
            <section className="panel panel--d1">
              <div className="panel__head">
                <h3 className="panel__title">Report</h3>
                <span className="panel__note">Loading</span>
              </div>
              <Skeleton rows={6} />
            </section>
          ) : null}

          {report.status === "error" ? (
            report.error instanceof ApiError && report.error.status === 404 ? (
              <EmptyState
                title="There is no report about this official."
                detail={report.error.message}
              />
            ) : (
              <FailBanner error={report.error} />
            )
          ) : null}

          {report.status === "ready" ? (
            <>
              {/*
                Keyed on the official, as the composer beside it is: a different official is a
                different report and a different notebook, never a mutated panel holding the
                previous person's draft while the fetch lands. The three keys are prefixed
                per-panel (`draft-`/`closing-`/`composer-`) — the official id alone repeats
                across these siblings, and a React key need be unique only among them, but a
                prefix costs nothing and removes the ambiguity outright.
              */}
              <AiDraftPanel key={`draft-${id}`} report={report.data} />
              {/*
                An umpire row only (§B5a) — run 46's own suggestion surface for the ONE box
                the TD keeps on that row, the closing remarks. `AiDraftPanel` above already
                explains, on this same row, that the NARRATIVE is the owning UM's; this panel
                is independent of that explanation and writes a different column entirely.
              */}
              {report.data.ownedByUm ? (
                <AiClosingNotePanel key={`closing-${id}`} report={report.data} />
              ) : null}
              <ReportComposer key={`composer-${id}`} officialName={name} initialReport={report.data} />
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
